import { Flex, HStack, Tab, TabList, Tabs, Text, type FlexProps } from '@chakra-ui/react';
import { type ReactNode } from 'react';

/**
 * UnderlineTabs — the in-panel tab row used by the order drawer sections
 * (Quoting, Ready to Order, Ordered) and invoice line items: muted labels,
 * the selected tab in `gray.800` over a `gray.500` underline, on a 1.5px
 * hairline. No brand fill and no blue text.
 *
 * Controlled: pass `value` + `onChange`. Row props (e.g. `mx={-6} px={6}`
 * to bleed to a padded container's edges) go on the hairline row.
 */
export interface UnderlineTabItem<T extends string = string> {
	value: T;
	label: ReactNode;
	/** Rendered as a small grey count chip after the label when > 0. */
	count?: number;
	isDisabled?: boolean;
}

export interface UnderlineTabsProps<T extends string = string> extends Omit<FlexProps, 'onChange'> {
	items: ReadonlyArray<UnderlineTabItem<T>>;
	value: T;
	onChange: (value: T) => void;
	/** Right-aligned content on the hairline row (actions, toggles). */
	actions?: ReactNode;
	/** `sm` for drawers and panels; `md` for a page-level row above a feed table. */
	size?: 'sm' | 'md';
	/**
	 * Thickness of the row's hairline. The selected underline overlaps exactly
	 * this much so it sits on the line instead of hanging below it.
	 */
	hairlineWidth?: '1px' | '1.5px';
}

const TAB_PY = { sm: 2, md: 3 } as const;

export function UnderlineTabs<T extends string = string>({
	items,
	value,
	onChange,
	actions,
	size = 'sm',
	hairlineWidth = '1.5px',
	...rest
}: UnderlineTabsProps<T>) {
	const index = Math.max(
		items.findIndex((item) => item.value === value),
		0,
	);
	return (
		<Tabs
			variant="unstyled"
			index={index}
			onChange={(next) => {
				const item = items[next];
				if (item) onChange(item.value);
			}}
		>
			<Flex
				justify="space-between"
				align="flex-end"
				borderBottomWidth={hairlineWidth}
				borderBottomStyle="solid"
				borderColor="gray.200"
				{...rest}
			>
				<TabList border="none" gap={0}>
					{items.map((item) => (
						<Tab
							key={item.value}
							isDisabled={item.isDisabled}
							fontWeight="medium"
							fontSize="sm"
							color="gray.500"
							bg="transparent"
							border="none"
							borderBottom="1.5px solid"
							borderColor="transparent"
							mb={`-${hairlineWidth}`}
							py={TAB_PY[size]}
							px={3}
							_selected={{
								color: 'gray.800',
								borderColor: 'gray.500',
							}}
							_hover={{
								color: 'gray.700',
							}}
						>
							<HStack spacing={0}>
								<Text>{item.label}</Text>
								{item.count != null && item.count > 0 && (
									<Flex
										ml={1.5}
										bg="gray.100"
										borderRadius="lg"
										h="18px"
										minW="18px"
										px="5px"
										align="center"
										justify="center"
										flexShrink={0}
									>
										<Text fontSize="11px" fontWeight="bold" lineHeight="1" color="gray.800">
											{item.count}
										</Text>
									</Flex>
								)}
							</HStack>
						</Tab>
					))}
				</TabList>
				{actions}
			</Flex>
		</Tabs>
	);
}

export default UnderlineTabs;
